import { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { fileService } from '../../services/fileService';
import { tryParseMediaProcessingResult } from '../../types/multimodal';
import { MediaProcessingStatus } from './MediaProcessingStatus';

export interface DocumentMessageProps {
  fileId: string;
  processedContentJson?: string | null;
}

/**
 * Renders a document attachment with extracted text (if available).
 */
export const DocumentMessage = ({ fileId, processedContentJson }: DocumentMessageProps) => {
  const result = useMemo(() => tryParseMediaProcessingResult(processedContentJson), [processedContentJson]);

  const download = async () => {
    const resp = await fileService.downloadFileBlob(fileId);
    const blob = resp.data;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attachment-${fileId}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">Document</Typography>
        <MediaProcessingStatus processedContentJson={processedContentJson} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={download} variant="outlined">
          Download
        </Button>
      </Box>

      {result?.caption && (
        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
          {result.caption}
        </Typography>
      )}

      {result?.extractedText && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
          {result.extractedText}
        </Typography>
      )}
    </Box>
  );
};

